import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';

const fmtTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export default function EmployeeMessages() {
  const [threads, setThreads] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newMsg, setNewMsg] = useState({ landlord_id: '', subject: '', body: '' });
  const bottomRef = useRef();

  const loadThreads = () => {
    api.get('/messages/employee/threads')
      .then(res => setThreads(res.data.threads || []))
      .catch(() => toast.error('Could not load messages'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadThreads();
    api.get('/employee/landlords').then(res => setLandlords(res.data.landlords || [])).catch(() => {});
  }, []);

  const openThread = (threadId) => {
    setActiveThread(threadId);
    setMsgLoading(true);
    api.get('/messages/employee/thread/' + threadId)
      .then(res => { setMessages(res.data.messages || []); loadThreads(); })
      .catch(() => toast.error('Could not load messages'))
      .finally(() => setMsgLoading(false));
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const thread = threads.find(t => t.thread_id === activeThread);
      const landlordId = thread?.sender_type === 'landlord' ? thread.sender_id : thread?.recipient_id;
      await api.post('/messages/employee/send', { landlord_id: landlordId, body: reply, thread_id: activeThread });
      setReply('');
      openThread(activeThread);
    } catch { toast.error('Could not send message'); }
    finally { setSending(false); }
  };

  const sendNew = async () => {
    if (!newMsg.landlord_id || !newMsg.body.trim()) { toast.error('Select a landlord and write a message'); return; }
    setSending(true);
    try {
      await api.post('/messages/employee/send', newMsg);
      toast.success('Message sent!');
      setShowNew(false);
      setNewMsg({ landlord_id: '', subject: '', body: '' });
      loadThreads();
    } catch { toast.error('Could not send message'); }
    finally { setSending(false); }
  };

  const activeThreadData = threads.find(t => t.thread_id === activeThread);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, display: 'flex', maxHeight: '100vh', overflow: 'hidden' }}>

        {/* Thread list */}
        <div style={{ width: 320, borderRight: '1px solid #e2e8f0', background: 'white', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Messages</div>
            <button onClick={() => setShowNew(!showNew)} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              + New
            </button>
          </div>

          {showNew && (
            <div style={{ padding: 14, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <select value={newMsg.landlord_id} onChange={e => setNewMsg(p => ({ ...p, landlord_id: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, marginBottom: 8, background: 'white' }}>
                <option value="">Select landlord...</option>
                {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name} — {l.email}</option>)}
              </select>
              <input value={newMsg.subject} onChange={e => setNewMsg(p => ({ ...p, subject: e.target.value }))}
                placeholder="Subject (optional)" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }} />
              <textarea value={newMsg.body} onChange={e => setNewMsg(p => ({ ...p, body: e.target.value }))}
                placeholder="Message..." style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, minHeight: 70, resize: 'none', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => setShowNew(false)} style={{ flex: 1, background: '#f1f5f9', border: 'none', borderRadius: 7, padding: '7px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                <button onClick={sendNew} disabled={sending} style={{ flex: 1, background: '#2563eb', color: 'white', border: 'none', borderRadius: 7, padding: '7px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Send</button>
              </div>
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Loading...</div> :
              threads.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No messages yet</div> :
              threads.map(t => (
                <div key={t.thread_id} onClick={() => openThread(t.thread_id)}
                  style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: activeThread === t.thread_id ? '#eff6ff' : 'white', transition: 'background 0.1s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontWeight: parseInt(t.unread_count) > 0 ? 700 : 600, fontSize: 13 }}>{t.sender_name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtTime(t.created_at)}</div>
                  </div>
                  {t.subject && <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, marginBottom: 2 }}>{t.subject}</div>}
                  <div style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.body}</div>
                  {parseInt(t.unread_count) > 0 && (
                    <span style={{ background: '#2563eb', color: 'white', borderRadius: 100, padding: '2px 8px', fontSize: 10, fontWeight: 700, marginTop: 4, display: 'inline-block' }}>
                      {t.unread_count} new
                    </span>
                  )}
                </div>
              ))
            }
          </div>
        </div>

        {/* Message thread */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {!activeThread ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Select a conversation</div>
                <div style={{ fontSize: 13 }}>Or start a new message to a landlord</div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: 'white', fontWeight: 700, fontSize: 15 }}>
                {activeThreadData?.subject || ('Conversation with ' + (activeThreadData?.sender_name || ''))}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {msgLoading ? <div style={{ textAlign: 'center', color: '#94a3b8' }}>Loading...</div> :
                  messages.map(m => {
                    const isEmployee = m.sender_type === 'employee';
                    return (
                      <div key={m.id} style={{ display: 'flex', justifyContent: isEmployee ? 'flex-end' : 'flex-start' }}>
                        <div style={{ maxWidth: '70%' }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, textAlign: isEmployee ? 'right' : 'left' }}>
                            {m.sender_name} · {fmtTime(m.created_at)}
                          </div>
                          <div style={{ background: isEmployee ? '#2563eb' : 'white', color: isEmployee ? 'white' : '#1e293b', borderRadius: isEmployee ? '14px 14px 4px 14px' : '14px 14px 14px 4px', padding: '10px 14px', fontSize: 14, border: isEmployee ? 'none' : '1px solid #e2e8f0', lineHeight: 1.5 }}>
                            {m.body}
                          </div>
                        </div>
                      </div>
                    );
                  })
                }
                <div ref={bottomRef} />
              </div>
              <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', background: 'white', display: 'flex', gap: 10 }}>
                <textarea value={reply} onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Type a message... (Enter to send)" rows={2}
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none' }} />
                <button onClick={sendReply} disabled={sending || !reply.trim()} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, padding: '0 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: !reply.trim() ? 0.5 : 1 }}>
                  Send
                </button>
              </div>
            </>
          )}
        </div>
        <LegalFooter />
      </div>
    </div>
  );
}
